import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Button, Form, Input, Typography } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { LOGIN_MUTATION } from '../../apollo/auth';
import { reconnectWS } from '../../apollo/client';
import useEThreeInit from '../../hooks/useEThreeInit';
import { UserRoles } from '../../types/User';
import { redirectForRole } from '../../utils';
import { ErrorLogger } from '../../utils/ErrorLogger';
import { LocalStorage } from '../../utils/LocalStorage';
import { messages, MessageTypes } from './messages';

const { Title } = Typography;
const { Password } = Input;

export interface LoginForm {
  email: string;
  password: string;
}

interface LocationState {
  email?: string;
}

const Login: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { loading: e3Loading, eThreeInit } = useEThreeInit();

  const initialValues = { email: location.state?.email };

  const [form] = Form.useForm();
  const [logIn, { loading, data }] = useMutation(LOGIN_MUTATION);

  const _logIn = useCallback(
    async (values: LoginForm) => {
      try {
        await logIn({ variables: { data: values } });
      } catch (e) {
        ErrorLogger.logError(e);
      }
    },
    [logIn]
  );

  useEffect(() => {
    async function proceedLogin() {
      if (data?.logInAdmin) {
        const { token, role } = data?.logInAdmin;

        LocalStorage.setItem('token', token);
        LocalStorage.setItem('role', role);

        if (role === UserRoles.RPM_PROVIDER) {
          await eThreeInit();
        }

        reconnectWS();
        redirectForRole(role, history);
      }
    }
    proceedLogin();
  }, [data, history, eThreeInit]);

  const onFinish = async (values: LoginForm) => {
    await form.validateFields();
    await _logIn(values);
  };

  return (
    <Form
      className="app__form app__form--auto app__form--center app__form--space-between"
      onFinish={onFinish}
      form={form}
      initialValues={initialValues}
    >
      <Title level={3}>Log in to Your Account</Title>
      <Form.Item
        name="email"
        rules={[
          {
            type: 'email',
            required: true,
            message: messages[MessageTypes.RequiredField],
          },
        ]}
      >
        <Input
          size="large"
          prefix={<UserOutlined className="site-form-item-icon" />}
          placeholder="Enter your email"
          type="email"
        />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          { required: true, message: messages[MessageTypes.RequiredField] },
        ]}
      >
        <Password
          size="large"
          prefix={<LockOutlined className="site-form-item-icon" />}
          type="password"
          placeholder="Enter your password"
          visibilityToggle
        />
      </Form.Item>
      <div className="app__form-bottom">
        <Form.Item>
          <Button
            size="large"
            loading={loading || e3Loading}
            type="primary"
            htmlType="submit"
            className="login-form-button"
          >
            Submit
          </Button>
        </Form.Item>
        <Form.Item>
          <Link to="/auth/forgot-password">Forgot your Password?</Link>
        </Form.Item>
      </div>
    </Form>
  );
};

export default Login;
